import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaInfoCircle,
  FaCheck,
  FaDna,
  FaHome,
  FaLeaf,
  FaPlus,
  FaSeedling,
  FaShieldAlt,
  FaAward,
  FaLightbulb,
  FaUsers,
  FaStar,
  FaChevronDown
} from 'react-icons/fa';

/* ============================================================
   LOCAL IMAGE IMPORTS
   Change filenames here if your actual filenames are different
============================================================ */

import ashrafiaLogo from '../logos/Ashrafiafarm/ashrafiafarmlogo.png';
import goatImage from '../logos/Ashrafiafarm/ashrafiafarm1.png';
import turkeyDumbaImage from '../logos/Ashrafiafarm/ashrafiafarm2.png';
import peshawariDumbaImage from '../logos/Ashrafiafarm/ashrafiafarm3.png';


export default function AshrafiaGoatFarm() {

  const navigate = useNavigate();

  /* ============================================================
     SCROLL REVEAL ANIMATION
  ============================================================ */

  useEffect(() => {

    const elements = document.querySelectorAll('.reveal');

    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }

        });

      },
      {
        threshold: 0.12
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();

  }, []);


  return (

    <div className="ashrafia-page">

      {/* ======================================================
          TOP NAVIGATION
      ====================================================== */}

      <header className="top-navigation">

        <div className="navigation-inner">

          <button
            className="back-button"
            onClick={() => navigate('/')}
          >
            <FaArrowLeft />
            <span>Back to All Sectors</span>
          </button>

          <div className="navigation-title">
            ASHRAFIA GOAT FARM
          </div>

          <div className="navigation-scroll">
            <FaChevronDown />
          </div>

        </div>

      </header>


      {/* ======================================================
          MAIN PAGE
      ====================================================== */}

      <main className="main-page">


        {/* ====================================================
            HERO SECTION
        ==================================================== */}

        <section className="hero-section">

          <div className="hero-overlay"></div>

          <div className="hero-content reveal">

            <div className="hero-logo-wrapper">

              <img
                src={ashrafiaLogo}
                alt="Ashrafia Goat Farm"
                className="hero-logo"
              />

            </div>

            <div className="hero-small-title">
              ASHRAF GROUP OF INDUSTRIES
            </div>

            <h1>
              ASHRAFIA
              <span>GOAT FARM</span>
            </h1>

            <p className="hero-description">
              PREMIUM LIVESTOCK BREEDING
              <br />
              &amp; FARM MANAGEMENT
            </p>

            <div className="hero-line"></div>

            <p className="hero-text">
              Premium livestock breeding, responsible farm management,
              strong genetics and healthy livestock for a sustainable future.
            </p>

            <button
              className="hero-button"
              onClick={() => {
                document
                  .getElementById('overview')
                  ?.scrollIntoView({
                    behavior: 'smooth'
                  });
              }}
            >
              EXPLORE OUR FARM
              <FaChevronDown />
            </button>

          </div>

        </section>


        {/* ====================================================
            OVERVIEW SECTION
        ==================================================== */}

        <section
          id="overview"
          className="overview-section section-container reveal"
        >

          <div className="section-heading">

            <div className="heading-icon">
              <FaInfoCircle />
            </div>

            <div>
              <span className="heading-small">
                ASHRAFIA GOAT FARM
              </span>

              <h2>
                PREMIUM LIVESTOCK
                <span>WITH TRUSTED HERITAGE</span>
              </h2>
            </div>

          </div>


          <div className="overview-grid">

            <div className="overview-content">

              <p>
                Ashrafia Goat Farm is dedicated to the breeding and
                rearing of high-quality livestock, combining traditional
                expertise with modern farming practices.
              </p>

              <p>
                We specialize in premium goat and fat-tailed sheep breeds,
                ensuring strong genetics, superior health, consistent
                quality and responsible livestock management.
              </p>


              <div className="overview-highlight">

                <div className="highlight-icon">
                  <FaDna />
                </div>

                <div>
                  <strong>
                    GENETICS &amp; QUALITY
                  </strong>

                  <span>
                    Selective breeding for superior livestock.
                  </span>
                </div>

              </div>


              <div className="overview-highlight">

                <div className="highlight-icon">
                  <FaShieldAlt />
                </div>

                <div>
                  <strong>
                    HEALTH &amp; CARE
                  </strong>

                  <span>
                    Hygienic environment and responsible care.
                  </span>
                </div>

              </div>


              <div className="overview-highlight">

                <div className="highlight-icon">
                  <FaLeaf />
                </div>

                <div>
                  <strong>
                    SUSTAINABILITY
                  </strong>

                  <span>
                    Ethical and sustainable farming practices.
                  </span>
                </div>

              </div>

            </div>


            <div className="overview-image-wrapper">

              <img
                src={goatImage}
                alt="Kashmiri Beetal and Rajanpuri goats"
                className="overview-image"
              />

              <div className="image-caption">
                KASHMIRI BEETAL &amp; RAJANPURI GOATS
              </div>

            </div>

          </div>

        </section>


        {/* ====================================================
            BREEDS SECTION
        ==================================================== */}

        <section className="breeds-section">

          <div className="section-container">

            <div className="center-heading reveal">

              <span>
                OUR LIVESTOCK
              </span>

              <h2>
                PREMIUM BREEDS
              </h2>

              <p>
                Carefully selected livestock with strong genetic lineage.
              </p>

            </div>


            <div className="breed-grid">


              {/* =================================================
                  BREED CARD 1
              ================================================= */}

              <div className="breed-card reveal">

                <div className="breed-image-wrapper">

                  <img
                    src={goatImage}
                    alt="Kashmiri Beetal Goats and Rajanpuri Goats"
                  />

                  <div className="breed-number">
                    01
                  </div>

                </div>

                <div className="breed-content">

                  <span className="breed-label">
                    PREMIUM GOATS
                  </span>

                  <h3>
                    KASHMIRI BEETAL
                    <br />
                    &amp; RAJANPURI GOATS
                  </h3>

                  <p>
                    Premium goat breeds selected for strong genetics,
                    healthy growth and superior livestock quality.
                  </p>

                  <div className="breed-check">
                    <FaCheck />
                    Strong Genetic Lineage
                  </div>

                  <div className="breed-check">
                    <FaCheck />
                    Premium Livestock Quality
                  </div>

                </div>

              </div>


              {/* =================================================
                  BREED CARD 2
              ================================================= */}

              <div className="breed-card reveal">

                <div className="breed-image-wrapper">

                  <img
                    src={turkeyDumbaImage}
                    alt="Turkey Dumba White Fat-Tailed Sheep"
                  />

                  <div className="breed-number">
                    02
                  </div>

                </div>

                <div className="breed-content">

                  <span className="breed-label">
                    FAT-TAILED SHEEP
                  </span>

                  <h3>
                    TURKEY DUMBA
                    <br />
                    <small>
                      WHITE FAT-TAILED SHEEP
                    </small>
                  </h3>

                  <p>
                    Healthy white fat-tailed sheep maintained under
                    controlled feeding and professional farm management.
                  </p>

                  <div className="breed-check">
                    <FaCheck />
                    Healthy Growth
                  </div>

                  <div className="breed-check">
                    <FaCheck />
                    Controlled Nutrition
                  </div>

                </div>

              </div>


              {/* =================================================
                  BREED CARD 3
              ================================================= */}

              <div className="breed-card reveal">

                <div className="breed-image-wrapper">

                  <img
                    src={peshawariDumbaImage}
                    alt="Peshawari Dumba Brown Fat-Tailed Sheep"
                  />

                  <div className="breed-number">
                    03
                  </div>

                </div>

                <div className="breed-content">

                  <span className="breed-label">
                    FAT-TAILED SHEEP
                  </span>

                  <h3>
                    PESHAWARI DUMBA
                    <br />
                    <small>
                      BROWN FAT-TAILED SHEEP
                    </small>
                  </h3>

                  <p>
                    Distinctive brown fat-tailed sheep bred with
                    attention to health, nutrition and livestock care.
                  </p>

                  <div className="breed-check">
                    <FaCheck />
                    Premium Bloodline
                  </div>

                  <div className="breed-check">
                    <FaCheck />
                    Quality Focused Care
                  </div>

                </div>

              </div>


            </div>

          </div>

        </section>


        {/* ====================================================
            EXPERTISE SECTION
        ==================================================== */}

        <section className="expertise-section section-container">

          <div className="center-heading reveal">

            <span>
              OUR EXPERTISE
            </span>

            <h2>
              MODERN FARM MANAGEMENT
            </h2>

            <p>
              Combining livestock knowledge with modern agricultural
              management practices.
            </p>

          </div>


          <div className="expertise-grid">


            <div className="expertise-card reveal">

              <div className="expertise-icon">
                <FaDna />
              </div>

              <h3>
                SELECTIVE BREEDING
              </h3>

              <p>
                Genetic selection and breeding programs focused on
                improving livestock quality and performance.
              </p>

            </div>


            <div className="expertise-card reveal">

              <div className="expertise-icon">
                <FaHome />
              </div>

              <h3>
                MODERN FACILITIES
              </h3>

              <p>
                Hygienic, organized and well-managed farm infrastructure
                for livestock welfare.
              </p>

            </div>


            <div className="expertise-card reveal">

              <div className="expertise-icon">
                <FaLeaf />
              </div>

              <h3>
                NUTRITION SYSTEMS
              </h3>

              <p>
                Controlled feeding and balanced nutrition programs
                supporting healthy livestock development.
              </p>

            </div>


            <div className="expertise-card reveal">

              <div className="expertise-icon">
                <FaPlus />
              </div>

              <h3>
                HEALTH FIRST
              </h3>

              <p>
                Regular health monitoring, disease prevention and
                hygienic livestock management.
              </p>

            </div>


            <div className="expertise-card reveal">

              <div className="expertise-icon">
                <FaSeedling />
              </div>

              <h3>
                SUSTAINABLE FARMING
              </h3>

              <p>
                Ethical farming practices designed for long-term
                livestock and environmental sustainability.
              </p>

            </div>


            <div className="expertise-card reveal">

              <div className="expertise-icon">
                <FaAward />
              </div>

              <h3>
                QUALITY CONTROL
              </h3>

              <p>
                Consistent monitoring and quality-focused livestock
                management from breeding to rearing.
              </p>

            </div>


          </div>

        </section>


        {/* ====================================================
            STRENGTH SECTION
        ==================================================== */}

        <section className="strength-section">

          <div className="section-container">

            <div className="center-heading light-heading reveal">

              <span>
                WHY ASHRAFIA
              </span>

              <h2>
                OUR STRENGTH
              </h2>

            </div>


            <div className="strength-grid">


              <div className="strength-item reveal">

                <div className="strength-icon">
                  <FaDna />
                </div>

                <strong>
                  PREMIUM GENETICS
                </strong>

                <p>
                  Strong bloodlines for superior livestock quality.
                </p>

              </div>


              <div className="strength-item reveal">

                <div className="strength-icon">
                  <FaHome />
                </div>

                <strong>
                  MODERN FACILITIES
                </strong>

                <p>
                  Hygienic and professionally managed farm infrastructure.
                </p>

              </div>


              <div className="strength-item reveal">

                <div className="strength-icon">
                  <FaLeaf />
                </div>

                <strong>
                  NUTRITION FOCUSED
                </strong>

                <p>
                  Balanced feeding programs for healthy growth.
                </p>

              </div>


              <div className="strength-item reveal">

                <div className="strength-icon">
                  <FaPlus />
                </div>

                <strong>
                  HEALTH FIRST
                </strong>

                <p>
                  Regular health checks and disease prevention.
                </p>

              </div>


              <div className="strength-item reveal">

                <div className="strength-icon">
                  <FaSeedling />
                </div>

                <strong>
                  SUSTAINABLE FUTURE
                </strong>

                <p>
                  Ethical practices designed for a better tomorrow.
                </p>

              </div>


            </div>

          </div>

        </section>


        {/* ====================================================
            FINAL TAGLINE
        ==================================================== */}

        <section className="final-tagline reveal">

          <div className="tagline-decoration">
            ✦
          </div>

          <h2>
            PREMIUM BREEDS.
            <span> HEALTHY LIVESTOCK.</span>
            <br />
            TRUSTED HERITAGE.
          </h2>

          <div className="tagline-decoration">
            ✦
          </div>

        </section>


        {/* ====================================================
            FOOTER
        ==================================================== */}

        <footer className="main-footer">

          <div className="footer-inner">


            <div className="footer-page-number">
              13
            </div>


            <div className="footer-company">

              <strong>
                ASHRAF GROUP OF INDUSTRIES
              </strong>

              <span>
                Building Legacy. Powering the Future.
              </span>

            </div>


            <div className="footer-values">


              <div>
                <FaShieldAlt />
                <span>TRUST</span>
              </div>


              <div>
                <FaAward />
                <span>QUALITY</span>
              </div>


              <div>
                <FaLightbulb />
                <span>INNOVATION</span>
              </div>


              <div>
                <FaUsers />
                <span>TEAMWORK</span>
              </div>


              <div>
                <FaStar />
                <span>EXCELLENCE</span>
              </div>


            </div>

          </div>

        </footer>


      </main>


      {/* ======================================================
          CSS
      ====================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }


        html {
          scroll-behavior: smooth;
        }


        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          background: #071426;
        }


        .ashrafia-page {
          min-height: 100vh;
          width: 100%;
          background: #ffffff;
          color: #172033;
          font-family:
            Arial,
            Helvetica,
            sans-serif;
          overflow-x: hidden;
        }


        /* =====================================================
           NAVIGATION
        ===================================================== */

        .top-navigation {
          position: sticky;
          top: 0;
          z-index: 1000;
          width: 100%;
          background:
            rgba(7, 20, 38, 0.96);
          backdrop-filter: blur(12px);
          border-bottom:
            1px solid rgba(212, 160, 52, 0.45);
        }


        .navigation-inner {
          width: 100%;
          max-width: 1600px;
          margin: auto;
          padding:
            13px 35px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }


        .back-button {
          border: 1px solid #d6a83e;
          background: transparent;
          color: #ffffff;
          padding:
            10px 18px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 9px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 700;
          transition:
            all 0.3s ease;
        }


        .back-button:hover {
          background: #d6a83e;
          color: #071426;
          transform:
            translateX(-4px);
        }


        .navigation-title {
          color: #d6a83e;
          font-size: 14px;
          font-weight: 800;
          letter-spacing: 3px;
        }


        .navigation-scroll {
          color: #d6a83e;
          animation:
            navigationBounce 1.5s infinite;
        }


        @keyframes navigationBounce {

          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(5px);
          }

        }


        /* =====================================================
           HERO
        ===================================================== */

        .hero-section {
          width: 100%;
          min-height: 680px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          overflow: hidden;
          background:
            linear-gradient(
              135deg,
              #071426,
              #102b49
            );
        }


        .hero-section::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            radial-gradient(
              circle at 50% 40%,
              rgba(214,160,52,0.18),
              transparent 40%
            );
          animation:
            heroGlow 6s ease-in-out infinite;
        }


        @keyframes heroGlow {

          0%,
          100% {
            transform: scale(1);
            opacity: 0.7;
          }

          50% {
            transform: scale(1.2);
            opacity: 1;
          }

        }


        .hero-overlay {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              180deg,
              rgba(7,20,38,0.2),
              rgba(7,20,38,0.9)
            );
        }


        .hero-content {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 900px;
          padding:
            80px 25px;
        }


        .hero-logo-wrapper {
          width: 180px;
          height: 180px;
          margin:
            0 auto 25px;
          border-radius: 50%;
          background: #ffffff;
          padding: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow:
            0 0 0 1px #d6a83e,
            0 0 35px rgba(214,160,52,0.25);
          animation:
            logoFloat 4s ease-in-out infinite;
        }


        @keyframes logoFloat {

          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-9px);
          }

        }


        .hero-logo {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }


        .hero-small-title {
          color: #d6a83e;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 5px;
          margin-bottom: 15px;
        }


        .hero-content h1 {
          margin: 0;
          color: #ffffff;
          font-family:
            Georgia,
            "Times New Roman",
            serif;
          font-size:
            clamp(48px, 7vw, 90px);
          line-height: 0.95;
          letter-spacing: 4px;
        }


        .hero-content h1 span {
          display: block;
          color: #d6a83e;
        }


        .hero-description {
          margin:
            28px 0 0;
          color: #ffffff;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 3px;
          line-height: 1.6;
        }


        .hero-line {
          width: 100px;
          height: 2px;
          background: #d6a83e;
          margin:
            25px auto;
        }


        .hero-text {
          max-width: 650px;
          margin: auto;
          color: #dbe4ef;
          font-size: 15px;
          line-height: 1.8;
        }


        .hero-button {
          margin-top: 30px;
          padding:
            14px 25px;
          border: 1px solid #d6a83e;
          background: #d6a83e;
          color: #071426;
          font-weight: 800;
          letter-spacing: 1px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition:
            all 0.3s ease;
        }


        .hero-button:hover {
          background: transparent;
          color: #ffffff;
          transform:
            translateY(-4px);
          box-shadow:
            0 10px 25px rgba(0,0,0,0.3);
        }


        /* =====================================================
           COMMON SECTION
        ===================================================== */

        .section-container {
          width: 100%;
          max-width: 1450px;
          margin: auto;
          padding:
            90px 45px;
        }


        .section-heading {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 55px;
        }


        .heading-icon {
          width: 58px;
          height: 58px;
          flex-shrink: 0;
          border-radius: 50%;
          background: #071426;
          color: #d6a83e;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }


        .heading-small {
          display: block;
          color: #b7831f;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 3px;
          margin-bottom: 8px;
        }


        .section-heading h2,
        .center-heading h2 {
          margin: 0;
          color: #071426;
          font-family:
            Georgia,
            "Times New Roman",
            serif;
          font-size:
            clamp(30px, 4vw, 48px);
          line-height: 1.1;
        }


        .section-heading h2 span {
          display: block;
          color: #b7831f;
        }


        /* =====================================================
           OVERVIEW
        ===================================================== */

        .overview-section {
          background: #ffffff;
        }


        .overview-grid {
          display: grid;
          grid-template-columns:
            1fr 1fr;
          gap: 60px;
          align-items: center;
        }


        .overview-content {
          color: #4a5568;
          font-size: 16px;
          line-height: 1.8;
        }


        .overview-content > p {
          margin:
            0 0 20px;
        }


        .overview-highlight {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-top: 18px;
          padding:
            16px 18px;
          background: #f8f5ec;
          border-left:
            3px solid #d6a83e;
          transition:
            all 0.3s ease;
        }


        .overview-highlight:hover {
          transform:
            translateX(8px);
          box-shadow:
            0 8px 25px rgba(0,0,0,0.08);
        }


        .highlight-icon {
          width: 40px;
          height: 40px;
          flex-shrink: 0;
          border-radius: 50%;
          background: #071426;
          color: #d6a83e;
          display: flex;
          align-items: center;
          justify-content: center;
        }


        .overview-highlight strong {
          display: block;
          color: #071426;
          font-size: 13px;
          letter-spacing: 1px;
        }


        .overview-highlight span {
          display: block;
          margin-top: 3px;
          font-size: 12px;
          color: #64748b;
        }


        .overview-image-wrapper {
          position: relative;
          height: 520px;
          overflow: hidden;
          border-radius: 4px;
          box-shadow:
            0 20px 45px rgba(7,20,38,0.2);
        }


        .overview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition:
            transform 0.8s ease;
        }


        .overview-image-wrapper:hover .overview-image {
          transform:
            scale(1.06);
        }


        .image-caption {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding:
            18px;
          text-align: center;
          background:
            linear-gradient(
              transparent,
              rgba(7,20,38,0.95)
            );
          color: #d6a83e;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 1px;
        }


        /* =====================================================
           BREEDS
        ===================================================== */

        .breeds-section {
          background: #f7f4ec;
          border-top:
            1px solid #e5d7b5;
          border-bottom:
            1px solid #e5d7b5;
        }


        .center-heading {
          text-align: center;
          margin-bottom: 55px;
        }


        .center-heading > span {
          display: block;
          color: #b7831f;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 4px;
          margin-bottom: 10px;
        }


        .center-heading p {
          max-width: 650px;
          margin:
            15px auto 0;
          color: #64748b;
          line-height: 1.7;
        }


        .breed-grid {
          display: grid;
          grid-template-columns:
            repeat(3, 1fr);
          gap: 25px;
        }


        .breed-card {
          background: #ffffff;
          overflow: hidden;
          box-shadow:
            0 10px 35px rgba(7,20,38,0.08);
          transition:
            all 0.45s ease;
          border:
            1px solid #e5d7b5;
        }


        .breed-card:hover {
          transform:
            translateY(-12px);
          box-shadow:
            0 25px 50px rgba(7,20,38,0.18);
        }


        .breed-image-wrapper {
          position: relative;
          height: 340px;
          overflow: hidden;
        }


        .breed-image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition:
            transform 0.7s ease;
        }


        .breed-card:hover .breed-image-wrapper img {
          transform:
            scale(1.08);
        }


        .breed-number {
          position: absolute;
          top: 15px;
          left: 15px;
          width: 45px;
          height: 45px;
          border-radius: 50%;
          background: #071426;
          color: #d6a83e;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 900;
          border:
            1px solid #d6a83e;
        }


        .breed-content {
          padding: 25px;
        }


        .breed-label {
          color: #b7831f;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 2px;
        }


        .breed-content h3 {
          margin:
            10px 0 15px;
          color: #071426;
          font-family:
            Georgia,
            "Times New Roman",
            serif;
          font-size: 22px;
          line-height: 1.15;
        }


        .breed-content h3 small {
          font-size: 12px;
          color: #b7831f;
        }


        .breed-content p {
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
        }


        .breed-check {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 10px;
          color: #334155;
          font-size: 12px;
          font-weight: 700;
        }


        .breed-check svg {
          color: #b7831f;
        }


        /* =====================================================
           EXPERTISE
        ===================================================== */

        .expertise-section {
          background: #ffffff;
        }


        .expertise-grid {
          display: grid;
          grid-template-columns:
            repeat(3, 1fr);
          gap: 20px;
        }


        .expertise-card {
          padding: 35px 28px;
          border:
            1px solid #e5e7eb;
          text-align: center;
          transition:
            all 0.4s ease;
          position: relative;
          overflow: hidden;
        }


        .expertise-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: #d6a83e;
          transform:
            scaleX(0);
          transform-origin: left;
          transition:
            transform 0.4s ease;
        }


        .expertise-card:hover::before {
          transform:
            scaleX(1);
        }


        .expertise-card:hover {
          transform:
            translateY(-8px);
          box-shadow:
            0 15px 35px rgba(7,20,38,0.1);
        }


        .expertise-icon {
          width: 65px;
          height: 65px;
          margin:
            0 auto 20px;
          border-radius: 50%;
          background: #071426;
          color: #d6a83e;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 25px;
          transition:
            all 0.4s ease;
        }


        .expertise-card:hover .expertise-icon {
          transform:
            rotateY(180deg);
        }


        .expertise-card h3 {
          color: #071426;
          font-size: 14px;
          letter-spacing: 1px;
          margin:
            0 0 12px;
        }


        .expertise-card p {
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
          margin: 0;
        }


        /* =====================================================
           STRENGTH
        ===================================================== */

        .strength-section {
          background:
            linear-gradient(
              135deg,
              #071426,
              #102b49
            );
          color: #ffffff;
        }


        .light-heading h2 {
          color: #ffffff;
        }


        .strength-grid {
          display: grid;
          grid-template-columns:
            repeat(5, 1fr);
          gap: 15px;
        }


        .strength-item {
          text-align: center;
          padding:
            25px 15px;
          border-right:
            1px solid rgba(214,160,62,0.35);
          transition:
            all 0.35s ease;
        }


        .strength-item:last-child {
          border-right: none;
        }


        .strength-item:hover {
          transform:
            translateY(-8px);
        }


        .strength-icon {
          width: 60px;
          height: 60px;
          margin:
            0 auto 15px;
          border-radius: 50%;
          background: #d6a83e;
          color: #071426;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 23px;
        }


        .strength-item strong {
          display: block;
          color: #d6a83e;
          font-size: 12px;
          letter-spacing: 1px;
        }


        .strength-item p {
          color: #cbd5e1;
          font-size: 11px;
          line-height: 1.6;
        }


        /* =====================================================
           FINAL TAGLINE
        ===================================================== */

        .final-tagline {
          width: 100%;
          padding:
            55px 25px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 30px;
          background: #ffffff;
          text-align: center;
        }


        .final-tagline h2 {
          margin: 0;
          color: #071426;
          font-family:
            Georgia,
            "Times New Roman",
            serif;
          font-size:
            clamp(24px, 3vw, 38px);
          letter-spacing: 1px;
        }


        .final-tagline h2 span {
          color: #b7831f;
        }


        .tagline-decoration {
          color: #d6a83e;
          font-size: 25px;
        }


        /* =====================================================
           FOOTER
        ===================================================== */

        .main-footer {
          background: #071426;
          border-top:
            2px solid #d6a83e;
          color: #ffffff;
        }


        .footer-inner {
          width: 100%;
          max-width: 1600px;
          margin: auto;
          padding:
            20px 45px;
          display: flex;
          align-items: center;
          gap: 25px;
        }


        .footer-page-number {
          font-size: 40px;
          font-weight: 900;
          color: #d6a83e;
          min-width: 55px;
        }


        .footer-company {
          display: flex;
          flex-direction: column;
          min-width: 300px;
        }


        .footer-company strong {
          color: #ffffff;
          font-size: 14px;
          letter-spacing: 1px;
        }


        .footer-company span {
          color: #d6a83e;
          font-size: 11px;
          margin-top: 5px;
        }


        .footer-values {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 30px;
        }


        .footer-values div {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          color: #ffffff;
          font-size: 8px;
          letter-spacing: 1px;
        }


        .footer-values svg {
          color: #d6a83e;
          font-size: 18px;
        }


        /* =====================================================
           SCROLL REVEAL
        ===================================================== */

        .reveal {
          opacity: 0;
          transform:
            translateY(35px);
          transition:
            opacity 0.8s ease,
            transform 0.8s ease;
        }


        .reveal.visible {
          opacity: 1;
          transform:
            translateY(0);
        }


        /* =====================================================
           TABLET
        ===================================================== */

        @media (max-width: 1100px) {

          .navigation-inner {
            padding:
              12px 20px;
          }


          .section-container {
            padding:
              75px 30px;
          }


          .overview-grid {
            gap: 35px;
          }


          .breed-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }


          .breed-card:last-child {
            grid-column:
              1 / -1;
            max-width: 500px;
            width: 100%;
            margin: auto;
          }


          .expertise-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }


          .strength-grid {
            grid-template-columns:
              repeat(3, 1fr);
          }


          .strength-item:nth-child(3) {
            border-right: none;
          }


          .footer-inner {
            padding:
              20px 30px;
            flex-wrap: wrap;
          }


          .footer-values {
            width: 100%;
            margin-left: 0;
            justify-content: center;
          }

        }


        /* =====================================================
           MOBILE
        ===================================================== */

        @media (max-width: 700px) {

          .top-navigation {
            position: relative;
          }


          .navigation-inner {
            padding:
              10px 14px;
          }


          .back-button {
            padding:
              9px 11px;
            font-size: 11px;
          }


          .navigation-title {
            display: none;
          }


          .navigation-scroll {
            display: none;
          }


          .hero-section {
            min-height: 600px;
          }


          .hero-content {
            padding:
              60px 20px;
          }


          .hero-logo-wrapper {
            width: 135px;
            height: 135px;
            padding: 10px;
          }


          .hero-small-title {
            font-size: 9px;
            letter-spacing: 2px;
          }


          .hero-content h1 {
            font-size:
              clamp(42px, 13vw, 65px);
            letter-spacing: 2px;
          }


          .hero-description {
            font-size: 12px;
            letter-spacing: 1.5px;
          }


          .hero-text {
            font-size: 13px;
          }


          .hero-button {
            font-size: 11px;
          }


          .section-container {
            padding:
              60px 18px;
          }


          .section-heading {
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 35px;
          }


          .heading-icon {
            width: 45px;
            height: 45px;
            font-size: 18px;
          }


          .section-heading h2 {
            font-size: 27px;
          }


          .overview-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }


          .overview-content {
            font-size: 14px;
          }


          .overview-image-wrapper {
            height: 350px;
          }


          .center-heading {
            margin-bottom: 35px;
          }


          .center-heading h2 {
            font-size: 30px;
          }


          .breed-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }


          .breed-card:last-child {
            grid-column: auto;
            max-width: none;
          }


          .breed-image-wrapper {
            height: 280px;
          }


          .breed-content {
            padding: 20px;
          }


          .expertise-grid {
            grid-template-columns: 1fr;
          }


          .expertise-card {
            padding:
              28px 22px;
          }


          .strength-grid {
            grid-template-columns:
              repeat(2, 1fr);
            gap: 0;
          }


          .strength-item {
            border-right:
              1px solid rgba(214,160,62,0.35);
            border-bottom:
              1px solid rgba(214,160,62,0.35);
          }


          .strength-item:nth-child(2n) {
            border-right: none;
          }


          .strength-item:last-child {
            grid-column:
              1 / -1;
            border-right: none;
          }


          .final-tagline {
            gap: 12px;
            padding:
              40px 15px;
          }


          .final-tagline h2 {
            font-size: 22px;
          }


          .tagline-decoration {
            display: none;
          }


          .footer-inner {
            padding:
              25px 18px;
            flex-direction: column;
            text-align: center;
            gap: 15px;
          }


          .footer-page-number {
            min-width: auto;
            font-size: 30px;
          }


          .footer-company {
            min-width: 0;
          }


          .footer-company strong {
            font-size: 12px;
          }


          .footer-values {
            display: grid;
            grid-template-columns:
              repeat(5, 1fr);
            gap: 8px;
            width: 100%;
          }


          .footer-values div {
            font-size: 7px;
          }


          .footer-values svg {
            font-size: 15px;
          }

        }


        /* =====================================================
           SMALL MOBILE
        ===================================================== */

        @media (max-width: 420px) {

          .back-button span {
            display: none;
          }


          .back-button {
            width: 38px;
            height: 38px;
            justify-content: center;
            padding: 0;
          }


          .hero-section {
            min-height: 560px;
          }


          .hero-logo-wrapper {
            width: 115px;
            height: 115px;
          }


          .hero-content h1 {
            font-size: 40px;
          }


          .hero-description {
            font-size: 10px;
          }


          .section-heading h2 {
            font-size: 23px;
          }


          .overview-image-wrapper {
            height: 290px;
          }


          .strength-grid {
            grid-template-columns: 1fr;
          }


          .strength-item,
          .strength-item:nth-child(2n) {
            border-right: none;
          }


          .strength-item:last-child {
            grid-column: auto;
          }


          .footer-values {
            gap: 3px;
          }

        }

      `}</style>

    </div>

  );
}